import React from 'react'
import { array, bool } from 'prop-types'
import { hasTag } from '@ycos/coremedia-renderer'
import { Grid } from '@ycos/component-grid'
import { ThemeProvider, withTheme, createComponent } from '@ycos/fela'
import SpanContainer from './SpanContainer'

// Left side
const CONFIG_LEFT = {
  'isc-two-column': {
    small: {
      width: 6,
      offset: 0
    },
    medium: {
      width: 5,
      offset: 1
    }
  },
  'isc-two-column-large': {
    small: {
      width: 6,
      offset: 0
    },
    medium: {
      width: 6,
      offset: 0
    }
  },
  'isc-two-column-desktop-only': {
    small: {
      width: 6,
      offset: 0
    },
    large: {
      width: 5,
      offset: 1
    }
  }
}

// Right side
const CONFIG_RIGHT = {
  'isc-two-column': {
    small: {
      width: 6,
      offset: 0
    },
    medium: {
      width: 5,
      offset: 0
    }
  },
  'isc-two-column-large': {
    small: {
      width: 6,
      offset: 0
    },
    medium: {
      width: 6,
      offset: 0
    }
  },
  'isc-two-column-desktop-only': {
    small: {
      width: 6,
      offset: 0
    },
    large: {
      width: 5,
      offset: 0
    }
  }
}

const FullBleedWrapper = createComponent('FullBleedWrapper', () => ({
  display: 'block',
  width: '100%',
  'screen-medium': {
    display: 'flex',
    justifyContent: 'space-between'
  }
}))

const FullBleedItem = createComponent('FullBleedItem', ({ theme, noPaddingTop, noPaddingTopMobile, addPaddingBottom, addPaddingBottomMobile, isSticky, offsetTop, centerContent, viewportHeight }) => {
  const { 
    spacingMultiplier, 
    rowOffsetTopSmall, 
    rowOffsetTopMedium, 
    rowOffsetTopLarge, 
    rowOffsetTopXLarge,
    rowOffsetBottomSmall, 
    rowOffsetBottomMedium,
    rowOffsetBottomLarge,
    rowOffsetBottomXLarge
  } = theme
  const topSmall = noPaddingTopMobile ? 0 : rowOffsetTopSmall * spacingMultiplier
  const topMedium = noPaddingTop ? 0 : rowOffsetTopMedium * spacingMultiplier
  const topLarge = noPaddingTop ? 0 : rowOffsetTopLarge * spacingMultiplier
  const topXLarge = noPaddingTop ? 0 : rowOffsetTopXLarge * spacingMultiplier
  const bottomSmall = addPaddingBottomMobile ? rowOffsetBottomSmall * spacingMultiplier : 0
  const bottomMedium = addPaddingBottom ? rowOffsetBottomMedium * spacingMultiplier : 0
  const bottomLarge = addPaddingBottom ? rowOffsetBottomLarge * spacingMultiplier : 0
  const bottomXLarge = addPaddingBottom ? rowOffsetBottomXLarge * spacingMultiplier : 0
  const displayAttribute = centerContent ? 'flex' : undefined

  return {
    width: '100%',
    paddingTop: `${topSmall}px`,
    paddingBottom: `${bottomSmall}px`,
    height: 'auto',
    position: 'relative',
    display: isSticky ? 'inline-block' : displayAttribute,
    flexDirection: centerContent ? 'column' : undefined,
    justifyContent: centerContent ? 'center' : undefined,

    'screen-medium': {
      width: '49%',
      position: isSticky ? 'sticky' : 'relative',
      top: isSticky ? `${offsetTop || -topMedium}px` : undefined,
      minHeight: viewportHeight ? '100vh' : undefined,
      paddingTop: `${topMedium}px`,
      paddingBottom: `${bottomMedium}px`
    },

    'screen-large': {
      top: isSticky ? `${offsetTop || -topLarge}px` : undefined,
      paddingTop: `${topLarge}px`,
      paddingBottom: `${bottomLarge}px`
    },

    'screen-xlarge': {
      top: isSticky ? `${offsetTop || -topXLarge}px` : undefined,
      paddingTop: `${topXLarge}px`,
      paddingBottom: `${bottomXLarge}px`
    }
  }
})

const desktopReverseStyles = {
  'screen-medium': {
    display: 'flex',
    flexDirection: 'row-reverse',
    justifyContent: 'flex-end'
  }
}

function TwoColumn({ children, item, verticallyCenter, isSticky, fullBleed, noPaddingTop, noPaddingTopMobile, addPaddingBottom, addPaddingBottomMobile, viewportHeight, desktopReverse, theme }) {
  const { layoutVariant = 'isc-two-column' } = item || {}
  const verticallyCenterState = verticallyCenter || hasTag(item && item.tags, 'Columns.VerticallyCenter')
  const sticky = isSticky || hasTag(item && item.tags, 'Columns.Sticky')
  const isFullBleed = fullBleed || hasTag(item && item.tags, 'Columns.FullBleed')
  const noTopPadding = noPaddingTop || hasTag(item && item.tags, 'Columns.NoTopPadding')
  const noTopPaddingMobile = noPaddingTopMobile || hasTag(item && item.tags, 'Columns.NoTopPaddingMobile')
  const addBottomPadding = addPaddingBottom || hasTag(item && item.tags, 'Columns.AddBottomPadding')
  const addBottomPaddingMobile = addPaddingBottomMobile || hasTag(item && item.tags, 'Columns.AddBottomPaddingMobile')
  const vhHeight = viewportHeight || hasTag(item && item.tags, 'Columns.ViewportHeight')
  const isDesktopReverse = desktopReverse || hasTag(item && item.tags, 'Columns.DesktopReverse')
  // If the whole column component is tagged with noPaddingTop, both columns will be flush. Otherwise only the children which have the tag

  const firstColumnComponent = children[0]?.props.item
  const firstColumnIsFlush = hasTag(firstColumnComponent?.tags, 'Columns.NoTopPadding') || noTopPadding
  const firstColumnIsFlushMobile = hasTag(firstColumnComponent?.tags, 'Columns.NoTopPaddingMobile') || noTopPaddingMobile

  const secondColumnComponent = children[1]?.props.item
  const secondColumnIsFlush = hasTag(secondColumnComponent?.tags, 'Columns.NoTopPadding') || noTopPadding
  const secondColumnIsFlushMobile = hasTag(secondColumnComponent?.tags, 'Columns.NoTopPaddingMobile') || noTopPaddingMobile

  const additionalStyles = isDesktopReverse ? desktopReverseStyles : {}

  return (
    <ThemeProvider theme={{ ...theme, direction: 'ltr' }}>
      {isFullBleed ? (
        <Grid.Row additionalStyles={additionalStyles} verticallyCenter={verticallyCenterState} fullBleed={isFullBleed}>
          <FullBleedWrapper>
            <FullBleedItem
              noPaddingTop={firstColumnIsFlush}
              noPaddingTopMobile={firstColumnIsFlushMobile}
              addPaddingBottom={addBottomPadding}
              addPaddingBottomMobile={addBottomPaddingMobile}
              isSticky={sticky}
              viewportHeight={vhHeight}
              centerContent={verticallyCenterState}
            >
              <ThemeProvider theme={theme}>{children[0]}</ThemeProvider>
            </FullBleedItem>
            <FullBleedItem
              noPaddingTop={firstColumnIsFlush}
              noPaddingTopMobile={firstColumnIsFlushMobile}
              addPaddingBottom={addBottomPadding}
              addPaddingBottomMobile={addBottomPaddingMobile}
              isSticky={sticky}
              viewportHeight={vhHeight}
              centerContent={verticallyCenterState}
            >
              <ThemeProvider theme={theme}>{children[1]}</ThemeProvider>
            </FullBleedItem>
          </FullBleedWrapper>
        </Grid.Row>
      ) : (
        <Grid.Row additionalStyles={additionalStyles} verticallyCenter={verticallyCenterState}>
          <Grid.Span small={CONFIG_LEFT[layoutVariant].small} medium={CONFIG_LEFT[layoutVariant].medium} large={CONFIG_LEFT[layoutVariant].large} xlarge={CONFIG_LEFT[layoutVariant].xlarge}>
            <SpanContainer
              noPaddingTop={firstColumnIsFlush}
              noPaddingTopMobile={firstColumnIsFlushMobile}
              addPaddingBottom={addBottomPadding}
              addPaddingBottomMobile={addBottomPaddingMobile}
              isSticky={sticky}
              viewportHeight={vhHeight}
              centerContent={verticallyCenterState}
            >
              <ThemeProvider theme={theme}>{children[0]}</ThemeProvider>
            </SpanContainer>
          </Grid.Span>
          <Grid.Span small={CONFIG_RIGHT[layoutVariant].small} medium={CONFIG_RIGHT[layoutVariant].medium} large={CONFIG_RIGHT[layoutVariant].large} xlarge={CONFIG_RIGHT[layoutVariant].xlarge}>
            <SpanContainer
              noPaddingTop={secondColumnIsFlush}
              noPaddingTopMobile={secondColumnIsFlushMobile}
              addPaddingBottom={addBottomPadding}
              addPaddingBottomMobile={addBottomPaddingMobile}
              isSticky={sticky}
              viewportHeight={vhHeight}
              centerContent={verticallyCenterState}
            >
              <ThemeProvider theme={theme}>{children[1]}</ThemeProvider>
            </SpanContainer>
          </Grid.Span>
        </Grid.Row>
      )}
    </ThemeProvider>
  )
}

TwoColumn.propTypes = {
  children: array,
  verticallyCenter: bool,
  isSticky: bool,
  fullBleed: bool,
  noPaddingTop: bool,
  noPaddingTopMobile: bool,
  addPaddingBottom: bool,
  addPaddingBottomMobile: bool,
  viewportHeight: bool,
  desktopReverse: bool
}

TwoColumn.defaultProps = {
  children: [],
  isSticky: false,
  fullBleed: false,
  noPaddingTop: false,
  noPaddingTopMobile: false,
  addPaddingBottom: false,
  addPaddingBottomMobile: false,
  viewportHeight: false,
  desktopReverse: false
}

TwoColumn.layoutVariant = ['isc-two-column', 'isc-two-column-large', 'isc-two-column-desktop-only']

export default withTheme(TwoColumn)
